import React, { memo, useMemo } from 'react';

import { imgEmptyActivities } from '@/assets/images';
import { Stack } from '@/components/layout';
import { Image, Skeleton, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import type { FamilyActivityItem } from '@/features/guardian/components/family-activity-section';
import FamilyActivityRow from '@/features/guardian/components/family-activity-section/row';
import { sortFamilyActivityItems } from '@/features/guardian/components/family-activity-section/utils';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';

const strings = STRINGS.guardianPlayer.coGuardianProfile;
const SKELETON_ROWS = 4;

type RecentActivitiesSectionProps = {
  items: FamilyActivityItem[];
  isLoading?: boolean;
};

/**
 * "Recent Activities" — the player-scoped feed for both guardians (BR-01). Rows are
 * rendered inline (not a nested list) since the whole profile already scrolls.
 */
function RecentActivitiesSection({ items, isLoading = false }: RecentActivitiesSectionProps) {
  const styles = useStyles();
  const { scale } = useScaling();

  const sortedItems = useMemo(() => sortFamilyActivityItems(items), [items]);
  const isInitialLoading = isLoading && sortedItems.length === 0;

  return (
    <Stack gap={scale({ phone: 12, tablet: 8 })}>
      <Text variant="Font Guardian App/Body/Body 16 Bold" style={styles.title}>
        {strings.recentActivitiesTitle}
      </Text>

      <Stack style={styles.card}>
        {isInitialLoading ? (
          <Stack gap={scale(16)} style={styles.skeletonWrap}>
            {Array.from({ length: SKELETON_ROWS }).map((_, index) => (
              <Stack key={index} direction="row" align="center" gap={scale(8)}>
                <Skeleton width={scale(32)} height={scale(32)} borderRadius={scale(16)} />
                <Skeleton width="70%" height={scale(16)} borderRadius={scale(4)} />
              </Stack>
            ))}
          </Stack>
        ) : sortedItems.length === 0 ? (
          <Stack align="center" justify="center" gap={scale(16)} style={styles.empty}>
            <Image
              source={imgEmptyActivities}
              width={scale(188)}
              height={scale(140)}
              resizeMode="contain"
            />
            <Text
              numberOfLines={1}
              variant="Font Guardian App/Subtitle/Subtitle 14 Semibold"
              style={styles.emptyText}>
              {strings.emptyActivities}
            </Text>
          </Stack>
        ) : (
          sortedItems.map(item => <FamilyActivityRow key={item.id} item={item} />)
        )}
      </Stack>
    </Stack>
  );
}

export default memo(RecentActivitiesSection);

const useStyles = createStyles(({ scale }) => ({
  title: {
    color: guardianColors['10'],
  },
  card: {
    backgroundColor: colors['0'],
    borderRadius: scale(12),
    borderCurve: 'continuous',
    paddingVertical: scale(8),
  },
  skeletonWrap: {
    padding: scale(12),
  },
  empty: {
    backgroundColor: colors['0'],
    borderRadius: scale(12),
    height: scale(287),
  },
  emptyText: {
    color: guardianColors['6'],
  },
}));
